import {
  ResponseResult,
  ResponseStatus,
  type QuestionResponseDetails,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import { deriveSubResponsesStatus } from './deriveSubResponsesStatus';
import { projectSubResponsesForPreservation } from './preserveResponseFields';

/**
 * The top-level "answer" fields the wire treats as the parent's
 * response: `responseStatus` and `responseResult`. Produced by
 * {@link deriveTopLevelFieldsForSave} and consumed by every
 * NOT-AN-ANSWER save builder (comment / media / naReason). The
 * builders assemble the outgoing payload from this pair without
 * re-deriving from `previousResponse.responseResult` themselves —
 * because that cached value is untrustworthy under a P/F-sub
 * layout (a derived FAIL-dominant aggregate written by the sub
 * answer optimistic patch), and re-asserting it hands the server
 * a wire shape it reads as a real parent "Fail All" answer, then
 * cascades to unanswered siblings.
 *
 * `responseResult` is optional by design: `undefined` on the type
 * means "server derives — omit from the wire." Callers hand this
 * straight to the mutation input without conditional spreads.
 */
export interface TopLevelSaveFields {
  readonly responseStatus: ResponseStatus;
  readonly responseResult?: ResponseResult;
}

/**
 * The subset of a cached parent response that NOT-AN-ANSWER save
 * builders are allowed to read. Excludes `responseResult` — the
 * server-derived aggregate that no builder should re-assert on a
 * non-answer save. If a future builder needs the top-level fields
 * it must go through {@link deriveTopLevelFieldsForSave}, which is
 * the only sanctioned reader of the untrustworthy aggregate.
 *
 * Passed to {@link buildPreservedResponseData} for parent-block
 * preservation (comment / naReason / mediaIds / taskIds — all
 * fields OWNED by the auditor, safe to re-assert verbatim).
 */
export type PreviousResponseForSave = Omit<QuestionResponseDetails, 'responseResult'>;

/**
 * Arguments for {@link deriveTopLevelFieldsForSave}.
 *
 * `previousResponse` here is the FULL cached record — this is the
 * ONE place in the codebase that reads its `responseResult`. The
 * builders that consume our output only receive the narrower
 * {@link PreviousResponseForSave} view.
 */
export interface DeriveTopLevelFieldsForSaveArgs {
  /**
   * The parent question's cached response, or undefined for a
   * never-answered question. Its `responseResult` is consulted
   * only when the parent OWNS its answer (see below).
   */
  readonly previousResponse: QuestionResponseDetails | undefined;
  /**
   * True when the parent has at least one currently VISIBLE
   * Pass/Fail sub-question. Match `useSubQuestionResponseDraft` /
   * `useBulkAnswerDraft` — both consume `visibleSubQuestions`.
   *
   * When true: the parent's aggregate is server-derived; we OMIT
   * `responseResult` from the wire and compute `responseStatus`
   * from the projected sub array via
   * {@link deriveSubResponsesStatus}.
   *
   * When false: the parent OWNS its answer (flat question, self-
   * scoring parent, or conditional parent whose current tap hides
   * every P/F sub); we re-assert `responseResult` from the cache
   * and map `responseStatus` in the traditional way (NA → NA,
   * PASS/FAIL → COMPLETED, undefined → PENDING).
   */
  readonly hasPassFailSubQuestions: boolean;
  /**
   * Total sub-questions on the parent (visible set — same view
   * `useSubQuestionResponseDraft` uses). Feeds
   * {@link deriveSubResponsesStatus}. Ignored when
   * `hasPassFailSubQuestions` is false.
   */
  readonly totalSubQuestions: number;
  /**
   * Existing sub-question responses on the parent (cached read
   * shape). The factory projects these via
   * {@link projectSubResponsesForPreservation} (no overrides —
   * results only) and hands the result to
   * {@link deriveSubResponsesStatus}.
   *
   * Rationale for using the cached rather than the outgoing array:
   * NOT-AN-ANSWER saves (comment / media / naReason) never change
   * a sub's `result`, so the wire-projected array and the cached
   * projection agree on every field `deriveSubResponsesStatus`
   * inspects. Reading from the cache lets the factory stay
   * independent of each domain's projection overrides.
   */
  readonly subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined;
}

/**
 * Compute the top-level `responseStatus` + `responseResult` that
 * a NOT-AN-ANSWER save (comment, media, NA reason, or any future
 * non-answer draft on this endpoint) needs to put on the wire.
 *
 * Central invariant: for a parent with any visible P/F children,
 * the client does NOT re-assert the cached `responseResult` on
 * the wire. The cached value is a DERIVED aggregate written by
 * the sub-answer optimistic patch (FAIL-dominant — one FAIL child
 * stamps the parent), and re-asserting it hands the server a
 * payload structurally indistinguishable from a real parent
 * answer, cascading FAIL to unanswered siblings on the next poll.
 * Same rule already documented on
 * `buildSubQuestionAnswerUpdate` and enforced by V1's
 * `saveQuestionResponse` guard.
 *
 * For flat / self-scoring parents the parent OWNS its answer, so
 * both fields are re-asserted from the cache. Status mapping is
 * NA → NA, PASS/FAIL → COMPLETED, undefined → PENDING (matches
 * `buildAnswerUpdate`).
 *
 * Pure — no react, no side effects. The single sanctioned reader
 * of `previousResponse.responseResult` for the save path.
 */
export function deriveTopLevelFieldsForSave(
  args: DeriveTopLevelFieldsForSaveArgs,
): TopLevelSaveFields {
  const {
    previousResponse,
    hasPassFailSubQuestions,
    totalSubQuestions,
    subQuestionResponses,
  } = args;

  if (hasPassFailSubQuestions) {
    const projected = projectSubResponsesForPreservation(subQuestionResponses);
    return {
      responseStatus: deriveSubResponsesStatus(projected, totalSubQuestions),
      // responseResult intentionally omitted — server derives.
    };
  }

  const previousResult = previousResponse?.responseResult;
  const responseStatus =
    previousResult === ResponseResult.NA
      ? ResponseStatus.NA
      : previousResult !== undefined
        ? ResponseStatus.COMPLETED
        : ResponseStatus.PENDING;

  if (previousResult === undefined) {
    return { responseStatus };
  }
  return { responseStatus, responseResult: previousResult };
}
